import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import leadPurchasesService from '../../services/leadPurchases';
import { Table, Button, Typography, Space, Tag, DatePicker, Row, Col, Card, Checkbox, Divider, Input, Modal, Segmented, Grid, message } from 'antd';
import { PrinterOutlined, EyeOutlined, DownloadOutlined, FilterOutlined, ClockCircleOutlined, SettingOutlined, DollarOutlined, RollbackOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import * as XLSX from 'xlsx'; // Import XLSX library
import ReturnRequestModal from '../../components/ReturnRequestModal';
import '../../styles/tables.css';

const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

// Add these styles at the top of the file after imports
const tableStyles = `
  .ant-table {
    width: 100% !important;
  }
  .ant-table-cell {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .fixed-actions-column {
    position: sticky !important;
    right: 0;
    background: white;
    z-index: 1;
  }
  /* Add table size styles */
  .table-size-large .ant-table-cell {
    padding: 16px 16px;
    font-size: 16px;
  }
  .table-size-middle .ant-table-cell {
    padding: 12px 12px;
    font-size: 14px;
  }
  .table-size-small .ant-table-cell {
    padding: 8px 8px;
    font-size: 12px;
  }
  /* Adjust row height for different sizes */
  .table-size-large .ant-table-tbody > tr > td {
    height: 64px;
  }
  .table-size-middle .ant-table-tbody > tr > td {
    height: 48px;
  }
  .table-size-small .ant-table-tbody > tr > td {
    height: 32px;
  }
`;

// Add the styles to the document
const styleSheet = document.createElement("style");
styleSheet.innerText = tableStyles;
document.head.appendChild(styleSheet);

// Reusable hook for column visibility
function useColumnVisibility(key, allColumns) {
  const storageKey = `column-visibility-${key}`;
  const [visibleKeys, setVisibleKeys] = React.useState(() => {
    const stored = localStorage.getItem(storageKey);
    if (stored) return JSON.parse(stored);
    return allColumns.map(col => col.key || col.dataIndex);
  });

  React.useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(visibleKeys));
  }, [visibleKeys, storageKey]);

  const setColumnVisibility = (key, visible) => {
    setVisibleKeys(prev => {
      if (visible) return Array.from(new Set([...prev, key]));
      return prev.filter(k => k !== key);
    });
  };

  const resetColumns = () => {
    setVisibleKeys(allColumns.map(col => col.key || col.dataIndex));
  };

  return {
    visibleKeys,
    setColumnVisibility,
    resetColumns,
    isVisible: (key) => visibleKeys.includes(key)
  };
}

// Helper for expanded row details
const getDetailsData = (record) => {
  if (!record) return [];
  const lead = record.lead || {};

  const basicInfo = [
    { field: 'Purchase Date', value: new Date(record.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) },
    { field: 'Transaction ID', value: record.transaction_id },
    { field: 'Amount', value: `$${record.amount}` },
    { field: 'Status', value: record.payment_status ? 'Completed' : 'Pending' }
  ];

  const personalInfo = [
    { field: 'First Name', value: lead.first_name },
    { field: 'Last Name', value: lead.last_name },
    { field: 'Phone', value: lead.phone },
    { field: 'Email', value: lead.email },
    { field: 'State', value: lead.state_name },
    { field: 'Birth Date', value: lead.birth_date }
  ];

  const insuranceInfo = [
    { field: 'Insurance Type', value: lead.type_of_life_insurance },
    { field: 'Coverage Amount', value: lead.coverage_amount },
    { field: 'Final Expense Coverage', value: lead.final_expense_coverage },
    { field: 'Beneficiary', value: lead.beneficiary },
    { field: 'Retirement Income', value: lead.additional_retirement_income },
    { field: 'Annual Income', value: lead.annual_income },
    { field: 'Do you have interest in an annuity?', value: lead.guaranteed_account_interest }
  ];

  const contactInfo = [
    // { field: 'Can We Contact', value: lead.can_we_contact === true ? 'Yes' : 'N/A' },
    { field: 'Mortgage Protection Amount', value: lead.mortgage_protection_amount },
    // { field: 'Funeral Type', value: lead.funeral_type },
    // { field: 'Similar Plan Contact Permission', value: lead.similar_plan_contact_permission === true ? 'Yes' : 'N/A' },
    // { field: 'Mortgage Amount Owed', value: lead.mortgage_amount_owed }
  ];

  return [
    { field: 'Purchase Information', value: '', isHeader: true },
    ...basicInfo,
    { field: 'Personal Information', value: '', isHeader: true },
    ...personalInfo,
    { field: 'Insurance Information', value: '', isHeader: true },
    ...insuranceInfo,
    { field: 'Contact & Mortgage Information', value: '', isHeader: true },
    ...contactInfo
  ];
};

const expandedRowRender = (record) => (
  <div style={{ padding: '16px' }}>
    <Table
      columns={[
        {
          title: 'Field',
          dataIndex: 'field',
          key: 'field',
          width: '30%',
        },
        {
          title: 'Value',
          dataIndex: 'value',
          key: 'value',
          width: '70%',
          render: (value, record) => {
            if (record.isHeader) {
              return <Typography.Text strong>{value}</Typography.Text>;
            }
            return value;
          }
        }
      ]}
      dataSource={getDetailsData(record)}
      pagination={false}
      rowKey={(record) => record.field}
      onRow={(record) => ({
        style: record.isHeader ? { 
          background: '#f5f5f5',
          fontWeight: 'bold'
        } : {}
      })}
      showHeader={false}
      size="small"
      style={{ background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
    />
  </div>
);

// Table size selector state
const getInitialTableSize = () => localStorage.getItem('purchases-table-size') || 'small';

const PurchasesPage = () => {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState(null);
  const [tableSize, setTableSize] = useState(getInitialTableSize());
  const [isReturnModalVisible, setIsReturnModalVisible] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });
  const screens = useBreakpoint();
  const queryClient = useQueryClient();

  const isToday = (date) => {
    const today = new Date();
    const purchaseDate = new Date(date);
    return (
      purchaseDate.getDate() === today.getDate() &&
      purchaseDate.getMonth() === today.getMonth() &&
      purchaseDate.getFullYear() === today.getFullYear()
    );
  };

  const { data: purchases, isLoading, error } = useQuery({
    queryKey: ['purchases', dateRange, pagination.current, pagination.pageSize],
    queryFn: () => {
      const params = {
        page: pagination.current,
        per_page: pagination.pageSize,
      };
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateRange[1].format('YYYY-MM-DD');
      }
      return leadPurchasesService.getPurchases(params);
    }
  });

  // Update pagination when data changes
  useEffect(() => {
    if (purchases?.meta) {
      setPagination(prev => ({
        ...prev,
        total: purchases.meta.total,
      }));
    }
  }, [purchases]);

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  const handleInvoice = (purchaseId) => {
    navigate(`/purchases/${purchaseId}/invoice`);
  };

  const handleDownloadExcel = () => {
    if (!purchases || !purchases.data) return;

    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric'
      });
    };

    const worksheet = XLSX.utils.json_to_sheet(
      purchases.data.map((purchase) => ({
        'Purchase Date': formatDate(purchase.created_at),
        'First Name': purchase.lead.first_name,
        'Last Name': purchase.lead.last_name,
        'Phone': purchase.lead.phone,
        'Email': purchase.lead.email,
        'Lead Age': formatDate(purchase.lead.lead_old),
        'Last Activity': (purchase.lead.last_activity),
        'State': purchase.lead.state_name,
        'Birth Date': (purchase.lead.birth_date),
        'Insurance Type': purchase.lead.type_of_life_insurance,
        'Coverage Amount': purchase.lead.coverage_amount,
        'Final Expense Coverage': purchase.lead.final_expense_coverage,
        'Beneficiary': purchase.lead.beneficiary,
        'Retirement Income': purchase.lead.additional_retirement_income,
        'Annual Income': purchase.lead.annual_income,
        'Do you have interest in an annuity?': purchase.lead.guaranteed_account_interest,
        // 'Can We Contact': purchase.lead.can_we_contact === true ? 'Yes' : 'N/A',
        'Mortgage Protection Amount': purchase.lead.mortgage_protection_amount,
        // 'Funeral Type': purchase.lead.funeral_type,
        // 'Similar Plan Contact Permission': purchase.lead.similar_plan_contact_permission === true ? 'Yes' : 'N/A',
        // 'Mortgage Amount Owed': purchase.lead.mortgage_amount_owed,
        'Amount': `$${purchase.amount}`,
        'Status': purchase.payment_status ? 'Completed' : 'Pending',
        'Transaction ID': purchase.transaction_id
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Purchases');
    XLSX.writeFile(workbook, 'purchases.xlsx');
  };

  const handleReturn = (purchase) => {
    // Check if there's already a return request
    if (purchase.returns && purchase.returns.length > 0) {
      message.warning('A return request already exists for this lead');
      return;
    }
    setSelectedPurchase(purchase);
    setIsReturnModalVisible(true);
  };

  const handleReturnSuccess = (purchaseId) => {
    // Update the cache immediately
    queryClient.setQueryData(['purchases', dateRange, pagination.current, pagination.pageSize], (oldData) => {
      if (!oldData) return oldData;
      
      return {
        ...oldData,
        data: oldData.data.map(purchase => {
          if (purchase.id === purchaseId) {
            return {
              ...purchase,
              returns: [{
                id: 'temp-id',
                status: 'pending',
                created_at: new Date().toISOString(),
                return_reason: 'Processing...'
              }]
            };
          }
          return purchase;
        })
      };
    });

    setIsReturnModalVisible(false);
    setSelectedPurchase(null);
  };

  const allTableColumns = [
    { title: 'First Name', key: 'first_name' },
    { title: 'Last Name', key: 'last_name' },
    { title: 'Email', key: 'email' },
    { title: 'Lead Age', key: 'lead_old' },
    { title: 'Last Activity', key: 'last_activity' },
    { title: 'State', key: 'state_name' },
    { title: 'Birth Date', key: 'birth_date' },
    { title: 'Insurance Type', key: 'type_of_life_insurance' },
    { title: 'Coverage Amount', key: 'coverage_amount' },
    { title: 'Final Expense Coverage', key: 'final_expense_coverage' },
    { title: 'Beneficiary', key: 'beneficiary' },
    { title: 'Are you looking for additional retirement income sources?', key: 'additional_retirement_income' },
    { title: 'Annual Income', key: 'annual_income' },
    { title: 'Do you have interest in an annuity?', key: 'guaranteed_account_interest' },
    // { title: 'Can We Contact', key: 'can_we_contact' },
    { title: 'Mortgage Protection Amount', key: 'mortgage_protection_amount' },
    // { title: 'Funeral Type', key: 'funeral_type' },
    // { title: 'Similar Plan Contact Permission', key: 'similar_plan_contact_permission' },
    // { title: 'Mortgage Amount Owed', key: 'mortgage_amount_owed' },
    { title: 'Amount', key: 'amount' },
    { title: 'Status', key: 'payment_status' },
    { title: 'Actions', key: 'actions' },
    { title: 'Purchase Date', key: 'created_at' },
  ];

  const {
    visibleKeys: visibleColumnKeys,
    setColumnVisibility,
    resetColumns,
    isVisible
  } = useColumnVisibility('purchases-table', allTableColumns);
  const [customizeModalOpen, setCustomizeModalOpen] = React.useState(false);
  const [columnSearch, setColumnSearch] = React.useState('');
  const filteredColumnOptions = allTableColumns.filter(col => {
    if (!columnSearch) return true;
    return (col.title || '').toLowerCase().includes(columnSearch.toLowerCase());
  });

  const columns = [
    {
      title: 'Purchase Date',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date, record) => (
        <Space>
          {new Date(date).toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric'
          })}
          {isToday(date) && (
            <Tag color="green" icon={<ClockCircleOutlined />}>
              Today
            </Tag>
          )}
        </Space>
      )
    },
    {
      title: 'First Name',
      dataIndex: ['lead', 'first_name'],
      key: 'first_name',
      width: 150,
    },
    {
      title: 'Last Name',
      dataIndex: ['lead', 'last_name'],
      key: 'last_name',
      width: 150,
    },
    {
      title: 'Email',
      dataIndex: ['lead', 'email'],
      key: 'email',
      width: 200,
    },
    {
      title: 'Lead Age',
      dataIndex: ['lead', 'lead_old'],
      key: 'lead_old',
      width: 150,
      render: (date) => {
        if (!date) return '';
        return new Date(date).toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric'
        });
      }
    },
    {
      title: 'Last Activity',
      dataIndex: ['lead', 'last_activity'],
      key: 'last_activity',
      width: 150,
    },
    {
      title: 'State',
      dataIndex: ['lead', 'state_name'],
      key: 'state_name',
      width: 120,
    },
    {
      title: 'Birth Date',
      dataIndex: ['lead', 'birth_date'],
      key: 'birth_date',
      width: 150,
    },
    {
      title: 'Insurance Type',
      dataIndex: ['lead', 'type_of_life_insurance'],
      key: 'type_of_life_insurance',
      width: 180,
    },
    {
      title: 'Coverage Amount',
      dataIndex: ['lead', 'coverage_amount'],
      key: 'coverage_amount',
      width: 150,
    },
    {
      title: 'Final Expense Coverage',
      dataIndex: ['lead', 'final_expense_coverage'],
      key: 'final_expense_coverage',
      width: 180,
    },
    {
      title: 'Beneficiary',
      dataIndex: ['lead', 'beneficiary'],
      key: 'beneficiary',
      width: 150,
    },
    {
      title: 'Are you looking for additional retirement income sources?',
      dataIndex: ['lead', 'additional_retirement_income'],
      key: 'additional_retirement_income',
      width: 240,
    },
    {
      title: 'Annual Income',
      dataIndex: ['lead', 'annual_income'],
      key: 'annual_income',
      width: 150,
    },
    {
      title: 'Do you have interest in an annuity?',
      dataIndex: ['lead', 'guaranteed_account_interest'],
      key: 'guaranteed_account_interest',
      width: 200,
    },
    // {
    //   title: 'Can We Contact',
    //   dataIndex: ['lead', 'can_we_contact'],
    //   key: 'can_we_contact',
    //   width: 150,
    // },
    {
      title: 'Mortgage Protection Amount',
      dataIndex: ['lead', 'mortgage_protection_amount'],
      key: 'mortgage_protection_amount',
      width: 200,
    },
    // {
    //   title: 'Funeral Type',
    //   dataIndex: ['lead', 'funeral_type'],
    //   key: 'funeral_type',
    //   width: 150,
    // },
    // {
    //   title: 'Similar Plan Contact Permission',
    //   dataIndex: ['lead', 'similar_plan_contact_permission'],
    //   key: 'similar_plan_contact_permission',
    //   width: 200,
    // },
    // {
    //   title: 'Mortgage Amount Owed',
    //   dataIndex: ['lead', 'mortgage_amount_owed'],
    //   key: 'mortgage_amount_owed',
    //   width: 180,
    // },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (amount) => `$${amount}`,
    },
    {
      title: 'Status',
      dataIndex: 'payment_status',
      key: 'payment_status',
      width: 120,
      render: (status) => (
        <Tag color={status ? 'green' : 'red'}>
          {status ? 'Completed' : 'Pending'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: screens.xs ? false : 'right',
      className: screens.xs ? '' : 'fixed-actions-column',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/purchases/${record.id}`)}
            title="View Details"
          />
          <Button
            type="link"
            icon={<PrinterOutlined />}
            onClick={() => handleInvoice(record.id)}
            title="View Invoice"
          />
          {record.returns && record.returns.length > 0 ? (
            <Button
              type="link"
              icon={
                record.returns[0].status === 'approved' ? (
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                ) : record.returns[0].status === 'rejected' ? (
                  <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                ) : (
                  <ClockCircleOutlined style={{ color: '#faad14' }} />
                )
              }
              title={
                record.returns[0].status === 'approved' ? 'Return Approved' :
                record.returns[0].status === 'rejected' ? 'Return Rejected' :
                'Return Pending'
              }
            />
          ) : (
            <Button
              type="link"
              icon={<RollbackOutlined />}
              onClick={() => handleReturn(record)}
              title="Request Return"
            />
          )}
        </Space>
      ),
    },
  ];

  const filteredColumns = columns.filter(col => isVisible(col.key));

  // Quick stats (example: total purchases, total amount, date range)
  const totalPurchases = purchases?.data?.length || 0;
  const totalAmount = purchases?.data?.reduce((sum, p) => sum + (Number(p.amount) || 0), 0) || 0;

  // Add these helper functions for chart data
  const getPurchaseTrendData = () => {
    const last30Days = Array.from({ length: 30 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - i);
      return date.toISOString().split('T')[0];
    }).reverse();

    return last30Days.map(date => {
      const dayPurchases = purchases?.data?.filter(p => 
        new Date(p.created_at).toISOString().split('T')[0] === date
      ) || [];
      return {
        date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        amount: dayPurchases.reduce((sum, p) => sum + Number(p.amount), 0),
        count: dayPurchases.length
      };
    });
  };

  const getStateDistributionData = () => {
    if (!purchases?.data?.length) {
      return [
        { name: 'No Data', value: 0, amount: 0 }
      ];
    }
    
    const stateData = purchases.data.reduce((acc, purchase) => {
      const state = purchase.lead.state_name;
      if (!acc[state]) {
        acc[state] = { count: 0, amount: 0 };
      }
      acc[state].count++;
      acc[state].amount += Number(purchase.amount);
      return acc;
    }, {});

    return Object.entries(stateData)
      .map(([state, data]) => ({
        name: state,
        value: data.count,
        amount: data.amount
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  };

  const handleTableSizeChange = (val) => {
    setTableSize(val);
    localStorage.setItem('purchases-table-size', val);
  };

  if (error) {
    return <Typography.Text type="danger">Error loading purchases: {error.message}</Typography.Text>;
  }

  return (
    <div className="page-container" style={{
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
      minHeight: '100vh',
      padding: '24px'
    }}>
      {/* Page Header */}
      <div className="page-header" style={{
        marginBottom: '32px',
        padding: '24px',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle at top right, rgba(24,144,255,0.1) 0%, rgba(24,144,255,0) 70%)',
          pointerEvents: 'none'
        }} />
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '24px',
          position: 'relative',
          zIndex: 1
        }}>
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '8px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(24,144,255,0.25)',
                transition: 'transform 0.3s ease'
              }}>
                <PrinterOutlined style={{ fontSize: '20px', color: '#fff' }} />
              </div>
              <Typography.Title level={2} style={{
                margin: 0,
                color: '#1f1f1f',
                background: 'linear-gradient(135deg, #1f1f1f 0%, #434343 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: '24px'
              }}>
                My Purchases
              </Typography.Title>
            </div>
            <Typography.Text type="secondary" style={{ fontSize: '12px', marginLeft: '52px' }}>
          View and manage your purchased leads
        </Typography.Text>
          </div>
        </div>
      </div>

      {/* Quick stats section */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <Card className="stat-card" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '36px', height: '36px', background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <EyeOutlined style={{ fontSize: '18px', color: '#fff' }} />
            </div>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: '12px' }}>Total Purchases</Typography.Text>
              <Typography.Title level={5} style={{ margin: '2px 0 0 0', fontSize: '14px' }}>{totalPurchases}</Typography.Title>
            </div>
          </div>
        </Card>
        <Card className="stat-card" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '36px', height: '36px', background: 'linear-gradient(135deg, #fa8c16 0%, #d46b08 100%)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <DollarOutlined style={{ fontSize: '18px', color: '#fff' }} />
            </div>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: '12px' }}>Total Amount</Typography.Text>
              <Typography.Title level={5} style={{ margin: '2px 0 0 0', fontSize: '14px' }}>${totalAmount.toLocaleString()}</Typography.Title>
            </div>
          </div>
        </Card>
        <Card className="stat-card" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '36px', height: '36px', background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ClockCircleOutlined style={{ fontSize: '18px', color: '#fff' }} />
            </div>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: '12px' }}>Date Range</Typography.Text>
              <Typography.Title level={5} style={{ margin: '2px 0 0 0', fontSize: '14px' }}>{dateRange && dateRange[0] && dateRange[1] ? `${dateRange[0].format('MMM D')} - ${dateRange[1].format('MMM D')}` : 'All Time'}</Typography.Title>
            </div>
          </div>
        </Card>
      </div>

      {/* Add Charts Section after Quick Stats - Always show charts */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '24px',
        marginBottom: '24px'
      }}>
        {/* Purchase Trend Chart */}
        <Card className="shadow-sm" style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <Typography.Title level={5}>Purchase Trend (Last 30 Days)</Typography.Title>
          <div style={{ height: '300px', width: '100%', minHeight: '250px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={getPurchaseTrendData()} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  interval="preserveStartEnd"
                  minTickGap={20}
                />
                <YAxis 
                  yAxisId="left" 
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  yAxisId="right" 
                  orientation="right" 
                  tick={{ fontSize: 12 }}
                />
                <Tooltip 
                  contentStyle={{ 
                    fontSize: '12px',
                    padding: '8px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                  }}
                />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="count"
                  stroke="#8884d8"
                  fill="#8884d8"
                  fillOpacity={0.3}
                  name="Number of Purchases"
                />
                <Area
                  yAxisId="right"
                  type="monotone"
                  dataKey="amount"
                  stroke="#82ca9d"
                  fill="#82ca9d"
                  fillOpacity={0.3}
                  name="Total Amount"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Top States Chart */}
        <Card className="shadow-sm" style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <Typography.Title level={5}>Top 5 States by Purchases</Typography.Title>
          <div style={{ height: '300px', width: '100%', minHeight: '250px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={getStateDistributionData()} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 12 }}
                  interval="preserveStartEnd"
                  minTickGap={20}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                />
                <Tooltip 
                  contentStyle={{ 
                    fontSize: '12px',
                    padding: '8px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                  }}
                />
                <Bar dataKey="value" fill="#8884d8" name="Number of Purchases" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Filter and actions card */}
      <Card className="shadow-sm" style={{ marginBottom: 24, borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={12}>
            <Space direction="vertical" style={{ width: '100%' }}>
              {/* <Typography.Text strong>Filter by Lead Age</Typography.Text> */}
              <RangePicker
                size="large"
                style={{ width: '100%', minHeight: '48px', fontSize: '16px', borderRadius: '8px' }}
                onChange={handleDateRangeChange}
                value={dateRange}
                placeholder={['Start Date', 'End Date']}
                allowClear
              />
            </Space>
          </Col>
          <Col xs={24} md={12} style={{ textAlign: 'right' }}>
            <Space wrap className="purchase-actions-responsive">
              <Button 
                size="large"
                icon={<FilterOutlined />}
                onClick={() => setDateRange(null)}
                style={{ height: '57px', fontSize: '16px', borderRadius: '8px' }}
              >
                Reset Filter
              </Button>
              <Button 
                type="primary" 
                size="large"
                icon={<DownloadOutlined />}
                onClick={handleDownloadExcel}
                style={{ minHeight: '48px', fontSize: '16px', borderRadius: '8px' }}
              >
                Download Excel
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Move Customize Columns button here, inside the Card above the Table */}
      <Card className="shadow-sm" style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <div className="table-controls-responsive" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Segmented
            options={[
              { label: 'Comfortable', value: 'large' },
              { label: 'Normal', value: 'middle' },
              { label: 'Compact', value: 'small' }
            ]}
            value={tableSize}
            onChange={handleTableSizeChange}
            style={{ marginRight: 12, minWidth: 120, width: 'auto' }}
          />
          <Button icon={<SettingOutlined />} onClick={() => setCustomizeModalOpen(true)}>
            Customize Columns
          </Button>
        </div>
        <div className="fixed-header-table" style={{ minHeight: '400px' }}>
        <Table
            columns={filteredColumns}
          dataSource={purchases?.data || []}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: 'max-content' }}
          size={tableSize}
          className={`ant-table table-size-${tableSize}`}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} items`,
            responsive: true,
          }}
          onChange={handleTableChange}
          locale={{
            emptyText: (
              <div className="table-empty-text" style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                height: '300px'
              }}>
                <Typography.Text type="secondary">
                  No purchases found
                </Typography.Text>
              </div>
            )
          }}
            expandable={{
              expandedRowRender,
              destroyOnClose: false
          }}
        />
        </div>
      </Card>

      <Modal
        title="Customize Columns"
        open={customizeModalOpen}
        onCancel={() => setCustomizeModalOpen(false)}
        footer={null}
        bodyStyle={{ padding: 24 }}
        width={600}
      >
        <div style={{ marginBottom: 12 }}>
          <Input
            placeholder="Search columns..."
            value={columnSearch}
            onChange={e => setColumnSearch(e.target.value)}
            allowClear
            size="large"
          />
        </div>
        <Divider style={{ margin: '12px 0' }} />
        <div className="customize-columns-grid" style={{
          maxHeight: 320,
          overflowY: 'auto',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 8,
          marginBottom: 16,
        }}>
          {filteredColumnOptions.map(col => (
            <Checkbox
              key={col.key}
              checked={isVisible(col.key)}
              onChange={e => setColumnVisibility(col.key, e.target.checked)}
              style={{ fontWeight: 400 }}
            >
              {col.title}
            </Checkbox>
          ))}
        </div>
        <Divider style={{ margin: '12px 0' }} />
        <Button type="default" block onClick={resetColumns} style={{ fontWeight: 400 }}>
          Reset to Default
        </Button>
      </Modal>

      <ReturnRequestModal
        visible={isReturnModalVisible}
        onClose={() => {
          setIsReturnModalVisible(false);
          setSelectedPurchase(null);
        }}
        purchase={selectedPurchase}
        onSuccess={handleReturnSuccess}
      />
    </div>
  );
};

export default PurchasesPage;